import api from './api';
import { uploadToUrl, uploadContentFile } from './uploadService';

/** Render a canvas to a JPEG Blob (promisified toBlob). */
export const canvasToJpeg = (canvas, quality = 0.92) =>
  new Promise((resolve, reject) => {
    try {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Could not render the story image'))),
        'image/jpeg',
        quality,
      );
    } catch (e) {
      // A tainted canvas (cross-origin background image without CORS) throws here.
      reject(new Error('Could not export the story — the background image blocked rendering (CORS).'));
    }
  });

/**
 * Flatten a styled story canvas and attach it to the content item.
 * Steps: canvas → JPEG → presigned S3 upload → /story/complete (sets editedS3Key + IMAGE kind).
 * Returns the updated content item.
 */
export const flattenAndSave = async (contentId, canvas, { onProgress } = {}) => {
  const blob = await canvasToJpeg(canvas);

  const urlRes = await api.post(`/api/v1/content/${contentId}/upload-url`, null, {
    params: { contentType: 'image/jpeg' },
  });
  const { uploadUrl, s3Key } = urlRes.data.data;

  await uploadToUrl(uploadUrl, blob, {
    contentType: 'image/jpeg',
    onProgress: (loaded, total) => onProgress?.(total > 0 ? Math.round((loaded / total) * 100) : 0),
  });

  const res = await api.put(`/api/v1/content/${contentId}/story/complete`, null, {
    params: { s3Key, fileSizeBytes: blob.size },
  });
  return res.data.data;
};

/**
 * Save a flattened video story: upload the composited mp4 (multipart-capable, so large exports work)
 * and finalize via /story/complete. Because the key ends in .mp4, the backend records the primary
 * media kind as VIDEO, so it publishes as a video story on every platform.
 * Returns the updated content item.
 */
export const flattenVideoAndSave = async (contentId, mp4Blob, { onProgress } = {}) => {
  const s3Key = await uploadContentFile(contentId, mp4Blob, { contentType: 'video/mp4', onProgress });
  const res = await api.put(`/api/v1/content/${contentId}/story/complete`, null, {
    params: { s3Key, fileSizeBytes: mp4Blob.size },
  });
  return res.data.data;
};

export default { flattenAndSave, flattenVideoAndSave, canvasToJpeg };
